<template>
  <section
    :class="{
      'homepage-top-header relative': true,
      'mt-100px md:mt-40 lg:mt-36': true,
    }"
  >
    <div
      :class="{
        'homepage-top-header-background relative xl:bg-[100%_100%]': true,
      }"
      :style="`background-image : url(${cdnImage(props.heroBackground?.asset?._ref).asIs}); background-repeat: no-repeat; background-position: center bottom;`"
    >
      <section
        :class="{
          'header-container fluid-container px-5 pb-20 lg:px-10': true,
        }"
      >
        <PersonalizedSectionBuilder
          v-if="closableEventBannerPersonalizedSectionBuilder"
          :data="closableEventBannerPersonalizedSectionBuilder"
          component-type="header-event-banner"
        />
        <div
          :class="{
            'grid grid-cols-12 gap-y-10 lg:gap-0': true,
          }"
        >
          <div
            :class="{
              'col-span-12 lg:col-span-6': true,
            }"
          >
            <PersonalizedSectionBuilder
              v-if="headerPersonalizedSectionBuilder"
              :data="headerPersonalizedSectionBuilder"
              component-type="header"
              :class="{
                'home-page-header mr-auto flex w-full flex-col items-center text-center text-www-color-neutral-medium-dark lg:w-[90%] lg:max-w-640px lg:text-left': true,
              }"
            />
          </div>
          <div
            :class="{
              'col-span-12 lg:col-span-6': true,
            }"
          >
            <PtcPicture
              v-if="props.heroImage && props.heroImage.asset"
              provider="sanity"
              :src="props.heroImage.asset._ref"
              :quality="90"
              loading="eager"
              :img-attrs="{
                class: { 'block h-auto w-full mx-auto lg:mr-auto': true },
              }"
              :modifiers="{
                crop: props.heroImage.crop,
                hotspot: props.heroImage.hotspot,
              }"
              :alt="resolvedImage?.altText || ''"
            />
          </div>
        </div>
      </section>
    </div>
  </section>
</template>

<script setup lang="ts">
import PtcPicture from '~/components/Atoms/PtcPicture/PtcPicture.vue';
import PersonalizedSectionBuilder from '~/components/Atoms/PersonalizedSectionBuilder/PersonalizedSectionBuilder.vue';
import { useRefsStore } from '~/composables/refsStore';
import type { HomePage, HomepagePersonalizedHero } from '~/sanity/types';
import * as AssertThat from '~/utils/AssertThat';

const props = defineProps<HomepagePersonalizedHero & Pick<HomePage, 'eventBanner'>>();

AssertThat.nonEmptyString(
  props.heroImage?.asset?._ref,
  'HomepagePersonalizedHero missing required heroImage'
);
AssertThat.nonEmptyString(
  props.heroBackground?.asset?._ref,
  'HomepagePersonalizedHero missing required heroBackground'
);

const { getResolvedPersonalizedSectionBuilder, getResolvedImage } = useRefsStore();
const resolvedImage = await getResolvedImage(props.heroImage?.asset?._ref);
const { productUsagePersona } = usePersonalization(); // this will make the call to crossSession

const { mouseFlowPush, dataLayerPush } = useTracking();
watch(
  productUsagePersona,
  () => {
    mouseFlowPush([
      'setVariable',
      'homepage-persona',
      `${productUsagePersona.value.persona}`,
      'session',
      false,
    ]);

    dataLayerPush({
      event: 'Personalization',
      category: `Personalization - ${productUsagePersona.value.persona}`,
      action: 'Viewed Version',
      label: productUsagePersona.value.persona || '',
    });
  },
  { immediate: true }
);

const headerPersonalizedSectionBuilder = await getResolvedPersonalizedSectionBuilder(
  props?.headerPersonalizedSectionBuilder?.reference?._ref
);

const closableEventBannerPersonalizedSectionBuilder = await getResolvedPersonalizedSectionBuilder(
  props?.eventBanner?.reference?._ref
);
</script>

<style lang="scss">
.homepage-top-header {
  @media (min-width: 1024px) and (max-width: 1699px) {
    .header-container {
      max-width: 100%;
    }
  }
  @media (min-width: 1700px) {
    .header-container {
      max-width: 1600px;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }
  .homepage-top-header-background {
    background-size: 100% auto;
  }
}

.home-page-header {
  .wistia_click_to_play {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }
  .wistia_click_to_play:after {
    border-bottom: 0.6rem solid transparent;
    border-left: 1rem solid;
    border-top: 0.6rem solid transparent;
    color: var(--www-color-primary-lighter);
    content: '';
    cursor: pointer;
    transition: all 0.4s ease-in-out;
  }
}
</style>
